import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, ArrowRight, CheckCircle2, Circle, MapPin, MoreVertical, Play, Send, Square, Trophy, UserPlus, Zap } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { useToast } from "@/hooks/use-toast";
import { sendProgressEvent } from "@/lib/progress";
import { cn } from "@/lib/utils";
import {
  deleteCircleInDb,
  fetchCircleById,
  fetchCircleEnergySnapshotInDb,
  fetchCircleMessageById,
  fetchCircleTaskCompletionCounts,
  getCircleMemberRole,
  isCircleMember,
  joinCircleInDb,
  leaveCircleInDb,
  listCircleMembers,
  listCircleMessages,
  listCircleTaskActivityFeed,
  listCircleTasksForMe,
  listCircleNumerologyMatchCandidatesInDb,
  listMyTaskCompletionsByDateKeys,
  removeCircleMemberInDb,
  sendBulkCircleInvitesInDb,
  sendCircleInviteByHandleInDb,
  sendCircleMessage,
  setCircleMemberRoleInDb,
  subscribeCircleMessages,
  toggleCircleTaskCompletionInDb,
  updateCircleInDb,
  type CircleMessage,
} from "@/lib/supabase/circles";
import { upsertMyRitualSessionForCircle } from "@/lib/supabase/rituals";

function initials(name: string) {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  const letters = parts.map((p) => p[0]?.toLocaleUpperCase("tr-TR") ?? "").join("");
  return letters || "?";
}

function formatTime(iso: string) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

function spiritualLevel(completedCount: number) {
  if (completedCount >= 10) return "Usta";
  if (completedCount >= 6) return "Dönüşen";
  if (completedCount >= 3) return "Uygulayıcı";
  if (completedCount >= 1) return "Arayışçı";
  return "Yeni";
}

function getDateKey(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getPastDateKeys(days: number) {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < days; i++) {
    const day = new Date(now);
    day.setDate(now.getDate() - i);
    out.push(getDateKey(day));
  }
  return out;
}

function mapCircleTaskPointsToXp(points: number) {
  const numeric = Math.max(0, Math.round(Number(points) || 0));
  if (numeric >= 3) return 50;
  if (numeric >= 2) return 30;
  return 15;
}

function computeStreakFromDateCounts(dateCounts: Record<string, number>) {
  let streak = 0;
  const now = new Date();
  for (let i = 0; i < 30; i++) {
    const day = new Date(now);
    day.setDate(now.getDate() - i);
    const key = getDateKey(day);
    if ((dateCounts[key] ?? 0) > 0) {
      streak += 1;
      continue;
    }
    break;
  }
  return streak;
}

type CircleDetailTab = "genel" | "gorev" | "akis" | "sohbet";

type ProgramItem = {
  id: string;
  title: string;
  coverImageUrl?: string;
  audioUrl?: string;
  active?: boolean;
};

type ProgramItemsResponse = {
  ok: true;
  items: ProgramItem[];
};

function normalizeTag(value: string) {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z]+/g, "");
}

function toPublicUrl(rawValue?: string) {
  const raw = String(rawValue ?? "").trim();
  if (!raw) return "";
  if (raw.startsWith("data:") || raw.startsWith("blob:") || raw.startsWith("/")) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  if (raw.startsWith("ftp://")) return raw.replace(/^ftp:\/\//i, "https://");
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return `https://${raw}`;
}

async function fetchProgramItems(path: string): Promise<ProgramItem[]> {
  const res = await fetch(path);
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Program verisi alınamadı.");
  }
  const data = json as ProgramItemsResponse;
  return Array.isArray(data?.items) ? data.items : [];
}

async function fetchMeditationPrograms() {
  return fetchProgramItems("/api/numeroloji/meditations");
}

async function fetchBreathPrograms() {
  return fetchProgramItems("/api/numeroloji/breaths");
}

async function fetchDietPrograms() {
  return fetchProgramItems("/api/numeroloji/diets");
}

function extractImageFromTaskDescription(description: string | null) {
  const lines = String(description ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const imageLine = lines.find((line) => line.toLocaleLowerCase("tr-TR").startsWith("görsel:"));
  if (!imageLine) return "";
  return toPublicUrl(imageLine.slice("görsel:".length).trim());
}

function findProgramByTitle(items: ProgramItem[], title: string) {
  const target = normalizeTag(title);
  if (!target) return null;
  return (
    items.find((item) => normalizeTag(item.title) === target) ??
    items.find((item) => normalizeTag(item.title).includes(target) || target.includes(normalizeTag(item.title))) ??
    null
  );
}

function parseTaskDescriptionForSteps(description: string | null) {
  const lines = String(description ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !line.toLocaleLowerCase("tr-TR").startsWith("plan:"))
    .filter((line) => !line.toLocaleLowerCase("tr-TR").startsWith("görsel:"));

  if (!lines.length) return { summary: "", steps: [] as string[] };
  if (lines.length > 1) return { summary: lines[0], steps: lines.slice(1) };

  const single = lines[0];
  if (!/\d+\./.test(single)) return { summary: single, steps: [] as string[] };

  const normalized = single.replace(/\s+/g, " ").trim();
  const matches = normalized.match(/\d+\.\s*[^\d]+(?=(\s+\d+\.|$))/g) ?? [];
  const steps = matches.map((item) => item.replace(/^\d+\.\s*/, "").trim()).filter(Boolean);
  return steps.length ? { summary: "", steps } : { summary: single, steps: [] as string[] };
}

function MembersPanel(props: {
  circleId: string;
  ownerId: string | null;
  myUserId: string;
  canManage: boolean;
  canPromote: boolean;
  onChanged: () => void;
}) {
  const { circleId, ownerId, myUserId, canManage, canPromote, onChanged } = props;

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [inviteHandle, setInviteHandle] = useState("");
  const [inviteAsAdmin, setInviteAsAdmin] = useState(false);
  const [actingUserId, setActingUserId] = useState<string | null>(null);
  const [memberActionsUserId, setMemberActionsUserId] = useState<string | null>(null);

  const membersQuery = useQuery({
    queryKey: ["circle-members", circleId],
    enabled: Boolean(circleId),
    queryFn: () => listCircleMembers(circleId),
    staleTime: 10_000,
  });

  const countsQuery = useQuery({
    queryKey: ["circle-task-counts", circleId],
    enabled: Boolean(circleId),
    queryFn: () => fetchCircleTaskCompletionCounts(circleId),
    staleTime: 5_000,
  });

  const autoMatchesQuery = useQuery({
    queryKey: ["circle-invite-numerology-matches", circleId],
    enabled: Boolean(circleId) && canManage,
    queryFn: () => listCircleNumerologyMatchCandidatesInDb({ circleId, limit: 8, minScore: 50 }),
    staleTime: 15_000,
    retry: 1,
  });

  const completionCounts = countsQuery.data ?? {};
  const members = membersQuery.data ?? [];
  const autoMatches = autoMatchesQuery.data ?? [];

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["circle-members", circleId] });
    await queryClient.invalidateQueries({ queryKey: ["circle", circleId] });
    await queryClient.invalidateQueries({ queryKey: ["circle-invite-numerology-matches", circleId] });
    onChanged();
  };

  const onKick = async (targetUserId: string) => {
    setActingUserId(targetUserId);
    try {
      await removeCircleMemberInDb({ circleId, userId: targetUserId });
      toast({ title: "Üye çıkarıldı" });
      await invalidate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "İşlem başarısız";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    } finally {
      setActingUserId(null);
    }
  };

  const onToggleAdmin = async (targetUserId: string, nextAdmin: boolean) => {
    setActingUserId(targetUserId);
    try {
      await setCircleMemberRoleInDb({ circleId, userId: targetUserId, role: nextAdmin ? "admin" : "member" });
      toast({ title: "Güncellendi" });
      await invalidate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "İşlem başarısız";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    } finally {
      setActingUserId(null);
    }
  };

  const onInvite = async () => {
    const handle = inviteHandle.trim();
    if (!handle) return;

    setActingUserId("invite");
    try {
      const result = await sendCircleInviteByHandleInDb({
        circleId,
        handle,
        inviteType: "manual",
        message: inviteAsAdmin ? "Yönetici rolü için değerlendirilen davet" : "Çemberine katılman için davet gönderildi",
      });
      setInviteHandle("");
      setInviteAsAdmin(false);
      toast({ title: result.fallbackMemberAdd ? "Doğrudan eklendi" : "Davet gönderildi", description: result.fallbackMemberAdd ? "Eski sistem uyumu nedeniyle üye doğrudan eklendi." : "Kullanıcı davet kutusundan kabul edebilir." });
      await invalidate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "İşlem başarısız";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    } finally {
      setActingUserId(null);
    }
  };

  const onInviteAutoMatch = async (handle: string, matchScore: number) => {
    const cleanHandle = handle.trim();
    if (!cleanHandle) return;

    setActingUserId(`auto-${cleanHandle}`);
    try {
      const result = await sendCircleInviteByHandleInDb({
        circleId,
        handle: cleanHandle,
        inviteType: "numerology_match",
        matchScore,
        message: `Çember kurallarına eşleşme oranı %${matchScore} olduğu için davet edildin.`,
      });

      toast({
        title: result.fallbackMemberAdd ? "Doğrudan eklendi" : "Davet gönderildi",
        description: result.fallbackMemberAdd ? "Eski sistem uyumu nedeniyle üye doğrudan eklendi." : `@${cleanHandle} için numeroloji daveti oluşturuldu.`,
      });
      await invalidate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Davet gönderilemedi";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    } finally {
      setActingUserId(null);
    }
  };

  const onInviteAllAutoMatches = async () => {
    if (!autoMatches.length) return;

    setActingUserId("auto-bulk");
    try {
      const result = await sendBulkCircleInvitesInDb({
        circleId,
        inviteType: "numerology_match",
        message: "Çember kurallarına eşleşme oranına göre çembere davet edildin.",
        invites: autoMatches.map((item) => ({ handle: item.handle, matchScore: item.matchScore })),
      });

      toast({
        title: `${result.sentCount} davet gönderildi`,
        description: result.errorCount > 0 ? `${result.errorCount} davet başarısız oldu.` : result.skippedCount > 0 ? `${result.skippedCount} kullanıcı zaten davetliydi.` : "Tüm eşleşmeler işlendi.",
      });

      await invalidate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Toplu davet gönderilemedi";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    } finally {
      setActingUserId(null);
    }
  };

  return (
    <aside className="flex h-[calc(100dvh-7.75rem)] min-h-[520px] flex-col overflow-hidden rounded-xl border border-violet-400/20 bg-slate-900/90 shadow-sm backdrop-blur-sm">
      <div className="border-b border-violet-400/15 p-5">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold uppercase tracking-wider text-white">Üyeler ({members.length})</div>
        </div>
      </div>

      <div className="min-h-[170px] flex-1 overflow-y-auto p-2">
        {membersQuery.isLoading ? (
          <div className="p-3 text-sm text-white/70">Yükleniyor...</div>
        ) : members.length ? (
          members.map((m) => {
            const isOwner = Boolean(ownerId && m.id === ownerId);
            const isMe = Boolean(myUserId && m.id === myUserId);
            const badge = isOwner ? "Sahip" : m.role === "admin" ? "Yönetici" : "Üye";
            const canActOnMember = canManage && !isOwner && !isMe;

            const completedCount = completionCounts[m.id] ?? 0;
            const level = spiritualLevel(completedCount);

            return (
              <div
                key={m.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-transparent p-3 transition-colors hover:border-violet-400/20 hover:bg-slate-900/60"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="relative">
                    <Avatar className="h-10 w-10 border-2 border-[var(--panel-primary)]/20">
                      <AvatarImage src={m.photoUrl ?? ""} alt={m.displayName} />
                      <AvatarFallback className="text-[10px]">{initials(m.displayName)}</AvatarFallback>
                    </Avatar>
                    <div className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-emerald-500 dark:border-slate-950" />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-white">{m.displayName}</div>
                    <div className="text-[10px] font-bold uppercase text-slate-400">
                      {badge}
                      {m.handle ? ` • @${m.handle}` : null}
                      {` • ${level} (${completedCount})`}
                    </div>
                  </div>
                </div>

                {canActOnMember ? (
                  <div className="relative shrink-0">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 rounded-md text-white/75 hover:bg-violet-500/15 hover:text-white"
                      aria-label={`${m.displayName} için işlemler`}
                      disabled={actingUserId === m.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMemberActionsUserId((prev) => (prev === m.id ? null : m.id));
                      }}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>

                    {memberActionsUserId === m.id ? (
                      <div
                        className="absolute right-0 top-9 z-20 flex w-44 flex-col gap-1 rounded-md border border-violet-400/25 bg-slate-950/95 p-1 shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {canPromote ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-8 justify-start rounded-sm px-2 text-left text-white hover:bg-violet-500/15"
                            disabled={actingUserId === m.id}
                            onClick={() => {
                              void onToggleAdmin(m.id, m.role !== "admin");
                              setMemberActionsUserId(null);
                            }}
                          >
                            {m.role === "admin" ? "Yönetici Kaldır" : "Yönetici Yap"}
                          </Button>
                        ) : null}

                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-8 justify-start rounded-sm px-2 text-left text-rose-400 hover:bg-rose-500/15 hover:text-rose-300"
                          disabled={actingUserId === m.id}
                          onClick={() => {
                            void onKick(m.id);
                            setMemberActionsUserId(null);
                          }}
                        >
                          At
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })
        ) : (
          <div className="p-3 text-sm text-white/70">Henüz üye yok.</div>
        )}
      </div>

      <div className="max-h-[55%] overflow-y-auto border-t border-violet-400/15 bg-slate-900/50 p-4">
        {canManage ? (
          <div className="space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-white/70">
              Üye / Yönetici Ekle
            </div>
            <div className="flex items-center gap-2">
              <Input
                value={inviteHandle}
                onChange={(e) => setInviteHandle(e.target.value)}
                placeholder="Handle (örn: ragip)"
                className="h-10 rounded-lg"
              />
              <Button className="rounded-lg" onClick={() => void onInvite()} disabled={actingUserId === "invite"}>
                Ekle
              </Button>
            </div>

            {canPromote ? (
              <label className="flex items-center gap-2 text-xs text-white/75">
                <Checkbox checked={inviteAsAdmin} onCheckedChange={(v) => setInviteAsAdmin(Boolean(v))} />
                Yönetici olarak ekle
              </label>
            ) : null}

            <Separator className="bg-violet-400/20" />

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-bold uppercase tracking-wider text-white/70">Numerolojik Eşleşmeler</div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 rounded-lg border-violet-400/35 bg-slate-900/60 text-[11px] text-white hover:bg-slate-900/80"
                  onClick={() => void onInviteAllAutoMatches()}
                  disabled={actingUserId === "auto-bulk" || !autoMatches.length || autoMatchesQuery.isLoading}
                >
                  {actingUserId === "auto-bulk" ? "Gönderiliyor..." : "Hepsini Davet Et"}
                </Button>
              </div>

              {autoMatchesQuery.isLoading ? (
                <div className="text-xs text-white/60">Eşleşmeler hesaplanıyor...</div>
              ) : autoMatches.length ? (
                <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                  {autoMatches.map((item) => (
                    <div key={item.userId} className="flex items-center justify-between gap-2 rounded-lg border border-violet-400/20 bg-slate-900/60 p-2">
                      <div className="min-w-0">
                        <div className="truncate text-xs font-semibold text-white">{item.displayName}</div>
                        <div className="truncate text-[11px] text-white/70">@{item.handle} • Kural eşleşmesi %{item.matchScore} ({item.matchedRules}/{item.totalRules})</div>
                      </div>
                      <Button
                        size="sm"
                        className="h-8 rounded-lg"
                        disabled={actingUserId === `auto-${item.handle}`}
                        onClick={() => void onInviteAutoMatch(item.handle, item.matchScore)}
                      >
                        Davet Et
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-white/60">Uygun numeroloji eşleşmesi bulunamadı.</div>
              )}
            </div>
          </div>
        ) : (
          <Button variant="secondary" className="w-full rounded-lg" disabled>
            <UserPlus className="mr-2 h-4 w-4" /> Arkadaşını Davet Et
          </Button>
        )}
      </div>
    </aside>
  );
}

function CircleChatPanel(props: {
  circleId: string;
  circleName: string;
}) {
  const { circleId, circleName } = props;
  const { user, loading, error } = useSupabaseSession();

  const [ready, setReady] = useState(false);
  const [messages, setMessages] = useState<CircleMessage[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement | null>(null);
  const myUserId = user?.id ?? "";
  const canChat = Boolean(!loading && user && !error);

  const scrollToBottom = () => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  useEffect(() => {
    if (!canChat) {
      setReady(false);
      setMessages([]);
      return;
    }

    let unsub: null | (() => void) = null;
    let cancelled = false;

    const boot = async () => {
      setChatError(null);
      setReady(false);
      try {
        const initial = await listCircleMessages({ circleId, limit: 120 });
        if (cancelled) return;
        setMessages(initial);
        setReady(true);
        queueMicrotask(scrollToBottom);

        unsub = subscribeCircleMessages({
          circleId,
          onInserted: async (id) => {
            try {
              const msg = await fetchCircleMessageById(id);
              if (!msg) return;
              setMessages((prev) => {
                if (prev.some((m) => m.id === msg.id)) return prev;
                return [...prev, msg].slice(-200);
              });
              queueMicrotask(scrollToBottom);
            } catch {
              // ignore
            }
          },
        });
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : typeof (err as any)?.message === "string"
              ? String((err as any).message)
              : "Sohbet başlatılamadı";
        if (!cancelled) setChatError(msg);
      }
    };

    void boot();

    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [canChat, circleId]);

  const onSend = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await sendCircleMessage({ circleId, body });
      setText("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Mesaj gönderilemedi";
      setChatError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex h-[calc(100dvh-7.75rem)] min-h-[520px] flex-col overflow-hidden rounded-xl border border-violet-400/20 bg-slate-900/90 shadow-sm backdrop-blur-sm">
      <div className="border-b border-violet-400/15 p-5">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="truncate font-bold text-white">{circleName}</div>
            <div className="text-xs text-white/70">Sohbet</div>
          </div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-white/60">{ready ? "Canlı" : "Bağlanıyor"}</div>
        </div>
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto p-4">
        {chatError ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/10 dark:text-rose-200">
            {chatError}
          </div>
        ) : null}

        <div className="space-y-3">
          {messages.map((m) => {
            const mine = myUserId && m.userId === myUserId;
            return (
              <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
                <div
                  className={cn(
                    "max-w-[80%] rounded-2xl px-4 py-3 text-sm",
                    mine
                      ? "bg-[var(--panel-primary)] text-white"
                      : "bg-slate-900 text-white",
                  )}
                >
                  <div className={cn("mb-1 text-[10px] font-bold uppercase tracking-wider opacity-70", mine && "text-white/80")}>
                    {m.senderName} • {formatTime(m.createdAt)}
                  </div>
                  <div className="whitespace-pre-wrap">{m.body}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border-t border-violet-400/15 p-4">
        <div className="flex items-center gap-2">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Mesaj yaz..."
            className="h-11 rounded-xl"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void onSend();
              }
            }}
          />
          <Button onClick={() => void onSend()} disabled={busy || !user} size="icon" aria-label="Gönder" className="rounded-xl">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}

export default function UserPanelCemberDetay() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user, loading } = useSupabaseSession();
  const { toast } = useToast();

  const circleId = String(params.id ?? "");

  const parseTab = (value: string | null): CircleDetailTab => {
    if (value === "gorev" || value === "akis" || value === "sohbet") return value;
    return "genel";
  };

  const [activeTab, setActiveTab] = useState<CircleDetailTab>(() => parseTab(searchParams.get("tab")));

  const circleQuery = useQuery({
    queryKey: ["circle", circleId],
    enabled: Boolean(circleId),
    queryFn: () => fetchCircleById(circleId),
    staleTime: 10_000,
  });

  const memberQuery = useQuery({
    queryKey: ["circle-member", circleId, user?.id],
    enabled: Boolean(circleId) && Boolean(user?.id) && !loading,
    queryFn: () => isCircleMember({ circleId, userId: String(user?.id ?? "") }),
    staleTime: 5_000,
  });

  const isMember = Boolean(memberQuery.data);
  const circle = circleQuery.data;

  const memberRoleQuery = useQuery({
    queryKey: ["circle-member-role", circleId, user?.id],
    enabled: Boolean(circleId) && Boolean(user?.id) && isMember && !loading,
    queryFn: () => getCircleMemberRole({ circleId, userId: String(user?.id ?? "") }),
    staleTime: 5_000,
  });

  const memberRole = memberRoleQuery.data ?? "member";

  const membersSnapshotQuery = useQuery({
    queryKey: ["circle-members", circleId],
    enabled: Boolean(circleId) && isMember,
    queryFn: () => listCircleMembers(circleId),
    staleTime: 10_000,
  });

  const completionCountsQuery = useQuery({
    queryKey: ["circle-task-counts", circleId],
    enabled: Boolean(circleId) && isMember,
    queryFn: () => fetchCircleTaskCompletionCounts(circleId),
    staleTime: 5_000,
  });

  const activityFeedQuery = useQuery({
    queryKey: ["circle-activity-feed", circleId],
    enabled: Boolean(circleId) && isMember,
    queryFn: () => listCircleTaskActivityFeed({ circleId, limit: 80 }),
    staleTime: 5_000,
  });

  const dailySnapshotQuery = useQuery({
    queryKey: ["circle-daily-snapshot", circleId],
    enabled: Boolean(circleId) && isMember,
    queryFn: () => fetchCircleEnergySnapshotInDb(circleId),
    staleTime: 15_000,
  });

  const tasksPreviewQuery = useQuery({
    queryKey: ["circle-tasks-preview", circleId, user?.id],
    enabled: Boolean(circleId) && isMember && Boolean(user?.id),
    queryFn: () => listCircleTasksForMe(circleId),
    staleTime: 5_000,
  });

  const meditationProgramsQuery = useQuery({
    queryKey: ["panel-meditation-programs"],
    queryFn: fetchMeditationPrograms,
    enabled: Boolean(isMember),
    staleTime: 60_000,
    retry: 1,
  });

  const breathProgramsQuery = useQuery({
    queryKey: ["panel-breath-programs"],
    queryFn: fetchBreathPrograms,
    enabled: Boolean(isMember),
    staleTime: 60_000,
    retry: 1,
  });

  const dietProgramsQuery = useQuery({
    queryKey: ["panel-diet-programs"],
    queryFn: fetchDietPrograms,
    enabled: Boolean(isMember),
    staleTime: 60_000,
    retry: 1,
  });

  const circleTasksPreviewQueryKey = ["circle-tasks-preview", circleId, user?.id] as const;

  const myRecentDateKeys = useMemo(() => getPastDateKeys(30), []);
  const myStreakQuery = useQuery({
    queryKey: ["circle-streak", user?.id, ...myRecentDateKeys],
    enabled: Boolean(user?.id) && isMember,
    queryFn: () => listMyTaskCompletionsByDateKeys(myRecentDateKeys),
    staleTime: 20_000,
  });

  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [mapEnabled, setMapEnabled] = useState(false);
  const [mapLocationBusy, setMapLocationBusy] = useState(false);
  const [mapLocation, setMapLocation] = useState<{ lat: number; lng: number } | null>(null);

  const membersSnapshot = membersSnapshotQuery.data ?? [];
  const completionCounts = completionCountsQuery.data ?? {};
  const tasksPreview = tasksPreviewQuery.data ?? [];

  const enrichedCircleTasks = useMemo(() => {
    const meditationPrograms = meditationProgramsQuery.data ?? [];
    const breathPrograms = breathProgramsQuery.data ?? [];
    const dietPrograms = dietProgramsQuery.data ?? [];

    return tasksPreview.map((task) => {
      const sourceProgram =
        task.kind === "meditasyon"
          ? findProgramByTitle(meditationPrograms, task.title)
          : task.kind === "nefes"
            ? findProgramByTitle(breathPrograms, task.title)
            : task.kind === "diyet"
              ? findProgramByTitle(dietPrograms, task.title)
              : null;

      const imageFromTask = extractImageFromTaskDescription(task.description);
      const coverImageUrl = toPublicUrl(sourceProgram?.coverImageUrl || imageFromTask);
      const audioUrl = toPublicUrl(sourceProgram?.audioUrl);
      const audioRequired = Boolean(audioUrl) && (task.kind === "meditasyon" || task.kind === "nefes");

      return {
        ...task,
        coverImageUrl: coverImageUrl || "",
        audioUrl: audioUrl || "",
        audioRequired,
      };
    });
  }, [breathProgramsQuery.data, dietProgramsQuery.data, meditationProgramsQuery.data, tasksPreview]);

  const ownerName = membersSnapshot.find((m) => m.id === circle?.ownerId)?.displayName ?? "Kurucu";
  const rankedMembers = [...membersSnapshot].sort((a, b) => (completionCounts[b.id] ?? 0) - (completionCounts[a.id] ?? 0));

  const activeParticipants = membersSnapshot.filter((m) => (completionCounts[m.id] ?? 0) > 0).length;
  const totalParticipants = membersSnapshot.length;
  const energyPercent = dailySnapshotQuery.data ? dailySnapshotQuery.data.energyScore : totalParticipants > 0 ? Math.round((activeParticipants / totalParticipants) * 100) : 0;
  const engagementScore = dailySnapshotQuery.data?.engagementScore ?? Math.max(0, Math.min(100, Math.round((Object.values(completionCounts).reduce((sum, n) => sum + n, 0) / Math.max(1, totalParticipants)) * 12)));

  const weeklyChampion = membersSnapshot.reduce<{ name: string; score: number }>(
    (best, m) => {
      const score = completionCounts[m.id] ?? 0;
      if (score > best.score) {
        return { name: m.displayName, score };
      }
      return best;
    },
    { name: "-", score: 0 },
  );

  const myStreakDays = computeStreakFromDateCounts(myStreakQuery.data ?? {});

  const feedItems = (activityFeedQuery.data ?? []).map((item) => ({
    id: item.id,
    taskId: item.taskId,
    taskTitle: item.taskTitle,
    userDisplayName: item.userDisplayName,
    time: item.createdAt ? formatTime(item.createdAt) : item.dateKey,
    dateKey: item.dateKey,
  }));

  const todayKey = getDateKey(new Date());
  const completionByTaskId = feedItems.reduce<Record<string, { todayUsers: string[]; totalCount: number }>>((acc, item) => {
    const prev = acc[item.taskId] ?? { todayUsers: [], totalCount: 0 };
    const nextUsers = [...prev.todayUsers];
    if (item.dateKey === todayKey && !nextUsers.includes(item.userDisplayName)) {
      nextUsers.push(item.userDisplayName);
    }
    acc[item.taskId] = {
      todayUsers: nextUsers,
      totalCount: prev.totalCount + 1,
    };
    return acc;
  }, {});

  const totalTaskCompletions = Object.values(completionCounts).reduce((sum, count) => sum + count, 0);
  const canEditCircle = Boolean(user?.id && isMember && (circle?.ownerId === user.id || memberRole === "admin"));

  useEffect(() => {
    if (!circle) return;
    setEditName(circle.name);
    setEditDescription(circle.description ?? "");
  }, [circle]);

  useEffect(() => {
    setActiveTab(parseTab(searchParams.get("tab")));
  }, [searchParams]);

  const goTab = (tab: CircleDetailTab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === "genel") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  const onJoin = async () => {
    if (!user?.id) {
      toast({ title: "Giriş gerekli", description: "Çembere katılmak için giriş yapmalısın.", variant: "destructive" });
      return;
    }

    try {
      await joinCircleInDb(circleId);
      await queryClient.invalidateQueries({ queryKey: ["circle-member", circleId, user.id] });
      await queryClient.invalidateQueries({ queryKey: ["circle-members", circleId] });
      await queryClient.invalidateQueries({ queryKey: ["circle", circleId] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Çembere katılınamadı";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    }
  };

  const onLeave = async () => {
    if (!user?.id) return;
    try {
      await leaveCircleInDb(circleId);
      await queryClient.invalidateQueries({ queryKey: ["circle-member", circleId, user.id] });
      await queryClient.invalidateQueries({ queryKey: ["circle-members", circleId] });
      await queryClient.invalidateQueries({ queryKey: ["circle", circleId] });
      navigate("/panel/cember");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Çemberden ayrılamadın";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    }
  };


  const onToggleTask = async (taskId: string) => {
    if (!isMember) return;

    const previousTasks = queryClient.getQueryData(circleTasksPreviewQueryKey) as typeof tasksPreviewQuery.data;
    const previousTask = Array.isArray(previousTasks) ? previousTasks.find((item) => item.id === taskId) : null;
    const optimisticCompleted = previousTask ? !previousTask.completed : false;

    queryClient.setQueryData(circleTasksPreviewQueryKey, (current: unknown) => {
      if (!Array.isArray(current)) return current;
      return current.map((item) => {
        if (!item || typeof item !== "object" || (item as { id?: string }).id !== taskId) return item;
        const taskItem = item as { completed?: boolean };
        return { ...item, completed: !Boolean(taskItem.completed) };
      });
    });

    setActiveTaskId((prev) => (prev === taskId || optimisticCompleted ? null : prev));

    try {
      const task = enrichedCircleTasks.find((item) => item.id === taskId);
      const dateKey = getDateKey(new Date());
      const completion = await toggleCircleTaskCompletionInDb({ circleId, taskId, dateKey });

      queryClient.setQueryData(circleTasksPreviewQueryKey, (current: unknown) => {
        if (!Array.isArray(current)) return current;
        return current.map((item) => {
          if (!item || typeof item !== "object" || (item as { id?: string }).id !== taskId) return item;
          return { ...item, completed: completion.completed };
        });
      });

      setActiveTaskId((prev) => (prev === taskId || completion.completed ? null : prev));

      if (completion.completed && task && user?.id) {
        const taskXp = mapCircleTaskPointsToXp(task.points);
        await sendProgressEvent({
          eventType: "task_completed",
          dateKey,
          sourceId: `circle:${circleId}:${task.id}:${dateKey}`,
          metadata: {
            source: "circle_task",
            circleId,
            taskId: task.id,
            taskKind: task.kind,
            title: task.title,
            taskPoints: taskXp,
            rawTaskPoints: task.points,
          },
        }).catch(() => undefined);
      }

      await queryClient.invalidateQueries({ queryKey: ["progress-summary", user?.id] });
      await queryClient.invalidateQueries({ queryKey: ["progress-timeline", user?.id] });
      await queryClient.invalidateQueries({ queryKey: ["progress-timeline-practices", user?.id] });
      await queryClient.invalidateQueries({ queryKey: circleTasksPreviewQueryKey });
      await queryClient.invalidateQueries({ queryKey: ["circle-task-counts", circleId] });
      await queryClient.invalidateQueries({ queryKey: ["circle-activity-feed", circleId] });
      await queryClient.invalidateQueries({ queryKey: ["circle-daily-snapshot", circleId] });
    } catch (err) {
      queryClient.setQueryData(circleTasksPreviewQueryKey, previousTasks ?? []);
      const msg = err instanceof Error ? err.message : "Görev güncellenemedi";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    }
  };


  const onTaskAction = (task: (typeof enrichedCircleTasks)[number], options?: { stopAudio?: boolean; fromAudioEnded?: boolean }) => {
    if (task.audioRequired) {
      if (options?.stopAudio) {
        setActiveTaskId((prev) => (prev === task.id ? null : prev));
        return;
      }

      if (task.completed) {
        void onToggleTask(task.id);
        return;
      }

      if (options?.fromAudioEnded) {
        void onToggleTask(task.id);
        return;
      }

      setActiveTaskId((prev) => (prev === task.id ? prev : task.id));
      return;
    }

    void onToggleTask(task.id);
  };

  const onPickMapLocation = () => {
    if (!navigator.geolocation) {
      toast({ title: "Konum desteklenmiyor", description: "Tarayıcın konum paylaşımını desteklemiyor." });
      return;
    }

    setMapLocationBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMapLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setMapEnabled(true);
        setMapLocationBusy(false);
      },
      () => {
        setMapLocationBusy(false);
        toast({
          title: "Konum alınamadı",
          description: "Konum iznini açıp tekrar dene.",
          variant: "destructive",
        });
      },
      { enableHighAccuracy: true, timeout: 12000 },
    );
  };

  const onSaveCircleSettings = async () => {
    if (!canEditCircle || !circle) return;

    const nextName = editName.trim();
    if (nextName.length < 2) {
      toast({ title: "Eksik bilgi", description: "Çember adı en az 2 karakter olmalı.", variant: "destructive" });
      return;
    }

    setEditBusy(true);
    try {
      await updateCircleInDb({
        circleId,
        name: nextName,
        description: editDescription,
        activity: circle.activity,
      });

      if (mapEnabled && mapLocation) {
        await upsertMyRitualSessionForCircle({
          circleId,
          title: `${nextName} Çember Konumu`,
          description: editDescription,
          lat: mapLocation.lat,
          lng: mapLocation.lng,
        });
      }

      await queryClient.invalidateQueries({ queryKey: ["circle", circleId] });
      await queryClient.invalidateQueries({ queryKey: ["panel-circles-mine", user?.id] });
      toast({ title: "Kaydedildi", description: "Çember ayarları güncellendi." });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Ayarlar kaydedilemedi";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    } finally {
      setEditBusy(false);
    }
  };

  const [deleteBusy, setDeleteBusy] = useState(false);
  const onDeleteCircle = async () => {
    if (!user?.id) return;
    if (deleteBusy) return;
    if (circle.ownerId !== user.id) return;

    const ok = window.confirm("Bu çemberi silmek istediğine emin misin? Bu işlem geri alınamaz.");
    if (!ok) return;

    setDeleteBusy(true);
    try {
      await deleteCircleInDb(circleId);
      await queryClient.invalidateQueries({ queryKey: ["panel-circles-mine", user.id] });
      await queryClient.invalidateQueries({ queryKey: ["panel-circles-discover", user.id] });
      await queryClient.invalidateQueries({ queryKey: ["circle-members", circleId] });
      await queryClient.invalidateQueries({ queryKey: ["circle", circleId] });
      navigate("/panel/cember");
      toast({ title: "Silindi", description: "Çember silindi." });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Çember silinemedi";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
      setDeleteBusy(false);
    }
  };

  const canManageMembers = Boolean(user?.id && isMember && (circle?.ownerId === user.id || memberRole === "admin"));
  const canPromoteMembers = Boolean(user?.id && circle?.ownerId === user.id);

  const onMembersChanged = () => {
    void queryClient.invalidateQueries({ queryKey: ["circle-members", circleId] });
    void queryClient.invalidateQueries({ queryKey: ["circle-member-role", circleId, user?.id] });
    void queryClient.invalidateQueries({ queryKey: ["circle-daily-snapshot", circleId] });
  };

  const renderTasksSection = () => (
    <section>
      <div className="mb-4 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
        <Zap className="h-5 w-5 text-slate-600 dark:text-white/80" />
        Çember Görevleri
      </div>
      <div className="space-y-3">
        {enrichedCircleTasks.length ? (
          enrichedCircleTasks.map((task) => {
            const parsed = parseTaskDescriptionForSteps(task.description);
            const completion = completionByTaskId[task.id] ?? { todayUsers: [], totalCount: 0 };
            const isActive = activeTaskId === task.id;

            return (
              <div
                key={task.id}
                className={cn(
                  "rounded-2xl border p-3 transition",
                  task.completed
                    ? "border-emerald-200 bg-emerald-50/70 dark:border-emerald-500/30 dark:bg-emerald-500/10"
                    : "border-slate-200 bg-white hover:border-[var(--panel-primary)]/35 dark:border-violet-400/20 dark:bg-slate-900 dark:hover:border-violet-400/45",
                )}
              >
                <div className="flex items-start gap-3">
                  {task.coverImageUrl ? (
                    <img
                      src={toPublicUrl(task.coverImageUrl)}
                      alt={task.title}
                      className={cn(
                        "rounded-xl object-cover",
                        task.audioRequired ? "h-40 w-full rounded-2xl sm:h-24 sm:w-40" : "h-16 w-16",
                      )}
                    />
                  ) : (
                    <div
                      className={cn(
                        "grid place-items-center rounded-xl bg-slate-100 text-slate-400 dark:bg-slate-800/70",
                        task.audioRequired ? "h-40 w-full rounded-2xl sm:h-24 sm:w-40" : "h-16 w-16",
                      )}
                    >
                      <Circle className="h-4 w-4" />
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                      {task.completed ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-slate-400" />}
                      <span className={cn(task.completed && "line-through opacity-70")}>{task.title}</span>
                    </div>

                    {parsed.summary ? (
                      <div className="mt-1 whitespace-pre-line text-xs text-slate-600 dark:text-slate-300">{parsed.summary}</div>
                    ) : (
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Açıklama eklenmedi.</div>
                    )}

                    {parsed.steps.length ? (
                      <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-slate-600 dark:text-slate-400">
                        {parsed.steps.map((step) => (
                          <li key={`${task.id}-${step}`}>{step}</li>
                        ))}
                      </ul>
                    ) : null}

                    {task.audioRequired && isActive && !task.completed ? (
                      <div className="mt-3 overflow-hidden rounded-xl border border-[var(--panel-primary)]/25 bg-[var(--panel-primary)]/5 p-2">
                        <audio
                          src={toPublicUrl(task.audioUrl)}
                          controls
                          autoPlay
                          preload="metadata"
                          className="w-full"
                          onEnded={() => onTaskAction(task, { fromAudioEnded: true })}
                        />
                        <div className="mt-2 text-[10px] font-bold text-[var(--panel-primary)]/90">Ses kaydı tamamlanınca görev işaretlenir.</div>
                      </div>
                    ) : null}

                    <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                      {completion.todayUsers.length
                        ? `Bugün tamamlayanlar: ${completion.todayUsers.join(", ")}`
                        : "Bugün henüz tamamlayan yok"}
                      {` • Toplam tamamlanma: ${completion.totalCount}`}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => onTaskAction(task, task.audioRequired && isActive && !task.completed ? { stopAudio: true } : undefined)}
                    className={cn(
                      "inline-flex items-center justify-center rounded-full text-xs font-extrabold",
                      task.audioRequired ? "self-end h-10 w-10 border border-[var(--panel-primary)]/35 bg-[var(--panel-primary)]/10 text-[var(--panel-primary)]" : "gap-2 px-4 py-2",
                      task.completed
                        ? "border-0 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                        : isActive
                          ? "border-0 bg-[var(--panel-primary)] text-white"
                          : !task.audioRequired
                            ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                            : "",
                    )}
                  >
                    {task.completed ? <CheckCircle2 className="h-3.5 w-3.5" /> : task.audioRequired ? (isActive ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />) : <ArrowRight className="h-3.5 w-3.5" />}
                    {!task.audioRequired ? (task.completed ? "Tamamlandı" : "Tamamla") : null}
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500 dark:border-violet-400/20 dark:bg-slate-900 dark:text-white/70">Henüz görev bulunamadı.</div>
        )}
      </div>
    </section>
  );

  const renderFlowSection = (limit?: number) => {
    const items = typeof limit === "number" ? feedItems.slice(0, limit) : feedItems;
    return (
      <section className="rounded-2xl border border-violet-400/20 bg-slate-900 p-5">
        <div className="mb-4 flex items-center gap-2 text-xl font-bold text-white">
          <Activity className="h-5 w-5 text-white/80" />
          Çember Akışı
        </div>
        {items.length ? (
          <div className="space-y-3">
            {items.map((item) => (
              <div key={item.id} className="rounded-xl border border-violet-400/20 bg-slate-950 p-3">
                <div className="text-sm text-white">
                  <span className="font-semibold">{item.userDisplayName}</span> “{item.taskTitle}” görevini tamamladı
                </div>
                <div className="mt-1 text-xs text-white/70">{item.time}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-white/70">Henüz akış kaydı yok.</div>
        )}
      </section>
    );
  };

  if (circleQuery.isLoading) {
    return (
      <div className="rounded-2xl border border-slate-100 bg-white p-6 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
        Çember yükleniyor...
      </div>
    );
  }

  if (!circle) {
    return (
      <div className="space-y-3">
        <div className="text-xl font-bold">Çember bulunamadı</div>
        <Button asChild variant="outline">
          <Link to="/panel/cember">Çemberlere dön</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-lg font-bold text-white">Çember Detay</div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="rounded-xl border-violet-400/30 bg-slate-900/60 text-white hover:bg-slate-900/80">
            <Link to="/panel/cember">Geri</Link>
          </Button>
          {circle.ownerId === user?.id ? (
            <Button variant="destructive" className="rounded-xl" disabled={deleteBusy} onClick={() => void onDeleteCircle()}>
              Çemberi Sil
            </Button>
          ) : null}
          {!isMember ? (
            <Button className="rounded-xl bg-violet-500 text-white hover:bg-violet-400" onClick={() => void onJoin()}>
              Katıl
            </Button>
          ) : (
            <Button variant="outline" className="rounded-xl border-violet-400/30 bg-slate-900/60 text-white hover:bg-slate-900/80" onClick={() => void onLeave()}>
              Ayrıl
            </Button>
          )}
        </div>
      </div>

      <section className="relative overflow-hidden rounded-[2rem] border border-violet-300/20 bg-gradient-to-br from-[#8f57f7] via-[#7d49f2] to-[#4d2bc2] px-6 py-8 shadow-[0_24px_80px_-30px_rgba(124,58,237,0.85)] md:px-10 md:py-10">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/15 blur-3xl" />
        <div className="absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-[#2c176b]/50 blur-3xl" />
        <div className="relative z-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="inline-flex rounded-full border border-white/20 bg-white/20 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white/90">Aktif Çember</div>
            <h1 className="mt-3 max-w-lg text-4xl font-black leading-tight text-white md:text-5xl">{circle.name}</h1>
            <div className="mt-5 flex items-center gap-4 text-sm text-white/80">
              <span>{ownerName}</span>
              <span className="h-1 w-1 rounded-full bg-white/40" />
              <span>{totalParticipants} Üye</span>
            </div>
          </div>
          <div className="w-full max-w-xs space-y-2">
            <div className="flex justify-between text-sm font-semibold text-white">
              <span>Enerji Seviyesi</span>
              <span>%{energyPercent}</span>
            </div>
            <div className="h-3 overflow-hidden rounded-full border border-white/10 bg-black/20">
              <div className="h-full rounded-full bg-white shadow-[0_0_18px_rgba(255,255,255,0.65)]" style={{ width: `${energyPercent}%` }} />
            </div>
            <div className="text-[11px] text-white/65">Yüksek titreşim algılandı • Etkileşim %{engagementScore}</div>
          </div>
        </div>
      </section>

      {canEditCircle ? (
        <section className="rounded-2xl border border-violet-400/20 bg-slate-950 p-5">
          <div className="text-lg font-bold text-white">Çember Ayarları</div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-white/80">Çember Adı</label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="rounded-xl border-violet-400/25 bg-slate-900 text-white" />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-white/80">Açıklama</label>
              <Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} className="rounded-xl border-violet-400/25 bg-slate-900 text-white" />
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-violet-400/20 bg-slate-900 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-white">Haritada göster</div>
                <div className="mt-1 text-xs text-white/70">Açık olursa bu çemberin konumu haritada görünür.</div>
              </div>
              <Checkbox checked={mapEnabled} onCheckedChange={(v) => setMapEnabled(Boolean(v))} />
            </div>

            {mapEnabled ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" className="rounded-xl border-violet-400/30 bg-slate-900/60 text-white hover:bg-slate-900/80" onClick={onPickMapLocation} disabled={mapLocationBusy}>
                  <MapPin className="mr-2 h-4 w-4" />
                  {mapLocationBusy ? "Konum alınıyor..." : mapLocation ? "Konumu güncelle" : "Konumumu al"}
                </Button>
                {mapLocation ? (
                  <span className="text-xs text-emerald-300">{mapLocation.lat.toFixed(5)}, {mapLocation.lng.toFixed(5)}</span>
                ) : (
                  <span className="text-xs text-white/70">Haritada göstermek için bir kez konum al.</span>
                )}
              </div>
            ) : null}
          </div>

          <div className="mt-4 flex justify-end">
            <Button className="rounded-xl" onClick={() => void onSaveCircleSettings()} disabled={editBusy || (mapEnabled && !mapLocation)}>
              {editBusy ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
            </Button>
          </div>
        </section>
      ) : null}

      <nav className="flex gap-2 overflow-x-auto no-scrollbar rounded-2xl bg-slate-900/40 p-1.5">
        {[
          { key: "genel", label: "Genel Bakış" },
          { key: "gorev", label: "Görevler" },
          { key: "akis", label: "Akış" },
          { key: "sohbet", label: "Sohbet" },
        ].map((tab) => {
          const selected = activeTab === (tab.key as CircleDetailTab);
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => goTab(tab.key as CircleDetailTab)}
              className={cn(
                "rounded-xl px-6 py-2.5 whitespace-nowrap",
                selected
                  ? "bg-violet-500/25 font-bold text-white"
                  : "font-medium text-white/75 hover:bg-violet-500/10",
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>

      {!isMember ? (
        <div className="rounded-2xl border border-violet-300/20 bg-violet-500/10 p-4 text-sm text-white">
          Bu çembere henüz dahil değilsin. “Katıl” diyerek görevleri, akışı ve sohbeti kullanabilirsin.
        </div>
      ) : activeTab === "sohbet" ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <CircleChatPanel circleId={circleId} circleName={circle.name} />
          <MembersPanel
            circleId={circleId}
            ownerId={circle.ownerId}
            myUserId={String(user?.id ?? "")}
            canManage={canManageMembers}
            canPromote={canPromoteMembers}
            onChanged={onMembersChanged}
          />
        </div>
      ) : activeTab === "gorev" ? (
        renderTasksSection()
      ) : activeTab === "akis" ? (
        renderFlowSection()
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="space-y-8">
            <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-violet-400/20 bg-slate-950 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-white/70">Toplam Üye</div>
                <div className="mt-2 text-2xl font-black text-white">{totalParticipants}</div>
              </div>
              <div className="rounded-2xl border border-violet-400/20 bg-slate-950 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-white/70">Aktif Katılımcı</div>
                <div className="mt-2 text-2xl font-black text-white">{activeParticipants}</div>
              </div>
              <div className="rounded-2xl border border-violet-400/20 bg-slate-950 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-white/70">Tamamlanan Görev</div>
                <div className="mt-2 text-2xl font-black text-white">{totalTaskCompletions}</div>
              </div>
              <div className="rounded-2xl border border-violet-400/20 bg-slate-950 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-white/70">Streak</div>
                <div className="mt-2 text-2xl font-black text-white">{myStreakDays} gün</div>
              </div>
            </section>

            {renderTasksSection()}
            {renderFlowSection(8)}
          </div>

          <aside className="space-y-8">
            <section className="rounded-2xl border border-violet-400/20 bg-slate-950 p-6">
              <div className="mb-4 flex items-center gap-2 text-lg font-bold text-white">
                <Trophy className="h-5 w-5 text-white/80" />
                Kolektife Katkı Sıralaması
              </div>
              <div className="space-y-3">
                {rankedMembers.map((member, index) => (
                  <div key={member.id} className="flex items-center gap-3 rounded-lg border border-violet-400/15 bg-slate-900/70 p-3">
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-violet-500/20 text-sm font-black text-white">{index + 1}</div>
                    <Avatar className="h-9 w-9 border border-violet-400/35">
                      <AvatarImage src={member.photoUrl ?? ""} alt={member.displayName} />
                      <AvatarFallback className="bg-violet-500/20 text-white">{initials(member.displayName)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-bold text-white">{member.displayName}</div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-white/70">{member.id === circle.ownerId ? "Kurucu" : spiritualLevel(completionCounts[member.id] ?? 0)}</div>
                    </div>
                    <div className="text-sm font-bold text-white">{completionCounts[member.id] ?? 0}</div>
                  </div>
                ))}
                {!rankedMembers.length ? <div className="text-sm text-white/70">Henüz katkı verisi yok.</div> : null}
              </div>
            </section>

            <section className="rounded-2xl border border-violet-400/20 bg-slate-950 p-6 text-center">
              <div className="font-bold italic text-white">Haftalık Vizyon</div>
              <div className="mt-3 text-sm text-white/80">"Zihnindeki berraklık, hayatındaki akışın aynasıdır."</div>
              <div className="mt-4 text-xs text-white/85">Haftanın enerji taşıyıcısı: {weeklyChampion.name}</div>
              <button type="button" className="mt-4 text-xs font-bold text-white underline underline-offset-4" onClick={() => goTab("akis")}>
                Tam Akışı Gör
              </button>
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}
